import { OnboardingSpecialty } from '@/lib/api/onboarding/onboarding-api'
import { Specialty } from '@/lib/api/common/interfaces/specialty.interface'
import { useState } from 'react'
import { MdAdd, MdClose } from 'react-icons/md'

interface Props {
    value: OnboardingSpecialty[]
    suggestions: Specialty[]
    onChange: (specialties: OnboardingSpecialty[]) => void
    /**
     * Avisa o wizard do que está digitado e ainda não virou item. É o que impede
     * "Continuar" de engolir a especialidade que o médico escreveu sem clicar em
     * Adicionar — o campo some junto com a etapa e o texto se perde em silêncio.
     */
    onDraftChange: (draft: string) => void
}

const normalize = (name: string): string =>
    (name ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()

/**
 * Especialidades do médico e, entre elas, em quais ele atende CONSULTA.
 *
 * As duas coisas na mesma tela porque a segunda pergunta só faz sentido depois da
 * primeira, e quem tem uma especialidade só não deveria ver duas etapas para
 * responder "sim".
 */
export default function SpecialtiesStep({
    value,
    suggestions,
    onChange,
    onDraftChange,
}: Props) {
    const [draft, setDraft] = useState('')

    const changeDraft = (text: string) => {
        setDraft(text)
        onDraftChange(text)
    }

    const add = (name: string, specialtyId?: number) => {
        const trimmed = name.trim()
        if (!trimmed) return
        if (value.some(item => normalize(item.name) === normalize(trimmed))) {
            changeDraft('')
            return
        }
        onChange([...value, { name: trimmed, specialtyId, consults: true }])
        changeDraft('')
    }

    const remove = (index: number) => {
        onChange(value.filter((_, i) => i !== index))
    }

    const toggleConsults = (index: number) => {
        onChange(
            value.map((item, i) =>
                i === index ? { ...item, consults: item.consults === false } : item
            )
        )
    }

    const available = suggestions.filter(
        suggestion => !value.some(item => normalize(item.name) === normalize(suggestion.name))
    )

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-3">
                <div className="form-control">
                    <label className="label pb-1" htmlFor="onboarding-specialty">
                        <span className="label-text font-medium">
                            Em quais especialidades você atua?
                        </span>
                    </label>
                    <div className="flex gap-2">
                        <input
                            id="onboarding-specialty"
                            className="input input-bordered w-full"
                            value={draft}
                            onChange={e => changeDraft(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    add(draft)
                                }
                            }}
                            placeholder="Cardiologia"
                        />
                        <button
                            type="button"
                            className="btn btn-secondary gap-1"
                            onClick={() => add(draft)}
                            disabled={!draft.trim()}
                        >
                            <MdAdd className="text-lg" />
                            Adicionar
                        </button>
                    </div>
                </div>

                {available.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        <span className="text-sm text-base-content/60 self-center">
                            Já cadastradas:
                        </span>
                        {available.slice(0, 12).map(suggestion => (
                            <button
                                key={suggestion.id}
                                type="button"
                                className="btn btn-xs btn-outline"
                                onClick={() => add(suggestion.name, suggestion.id)}
                            >
                                {suggestion.name}
                            </button>
                        ))}
                    </div>
                )}
            </section>

            {value.length > 0 && (
                <section className="flex flex-col gap-3">
                    <div>
                        <h3 className="font-semibold text-base-content">
                            Em quais delas você faz consulta?
                        </h3>
                        <p className="text-sm text-base-content/60">
                            Só as marcadas ganham um item de consulta no catálogo, com o
                            valor que você definir na próxima etapa.
                        </p>
                    </div>

                    <ul className="flex flex-col gap-2">
                        {value.map((specialty, index) => (
                            <li
                                key={`${specialty.name}-${index}`}
                                className="flex items-center justify-between gap-3 rounded-lg border border-base-300 px-4 py-3"
                            >
                                <label className="label cursor-pointer gap-3 justify-start p-0">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={specialty.consults !== false}
                                        onChange={() => toggleConsults(index)}
                                    />
                                    <span className="label-text">{specialty.name}</span>
                                </label>

                                <button
                                    type="button"
                                    className="btn btn-ghost btn-sm btn-circle"
                                    onClick={() => remove(index)}
                                    aria-label={`Remover ${specialty.name}`}
                                >
                                    <MdClose className="text-lg" />
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    )
}
